import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import {
  listUsers,
  createUser,
  updateUserStatus,
  updateUserRole,
  type User,
  type CreateUserRequest,
} from "@app/services/adminUsersApi";
import { logger } from "@app/services/logger";
import { UserDiagnosticsModal } from "@app/components/admin/UserDiagnosticsModal";
import { UserProfileModal } from "@app/components/admin/UserProfileModal";
import "./users.css";

export function UsersTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    email: "",
    name: "",
    role: "user",
    status: "active",
  });

  // Fetch users
  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminUsers"],
    queryFn: () => listUsers(),
  });

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (user: CreateUserRequest) => createUser(user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      setShowCreateModal(false);
      setCreateForm({ email: "", name: "", role: "user", status: "active" });
    },
    onError: (error: any) => {
      logger.error("Failed to create user", error);
      alert(`Failed to create user: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: "active" | "disabled" }) =>
      updateUserStatus(userId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
    },
    onError: (error: any) => {
      logger.error("Failed to update user status", error);
      alert(`Failed to update status: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: "admin" | "user" }) =>
      updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
    },
    onError: (error: any) => {
      logger.error("Failed to update user role", error);
      alert(`Failed to update role: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleCreate = () => {
    if (!createForm.email) {
      alert("Email is required");
      return;
    }
    createMutation.mutate(createForm);
  };

  const handleToggleStatus = (user: User) => {
    const newStatus = user.status === "active" ? "disabled" : "active";
    if (confirm(`Are you sure you want to ${newStatus === "disabled" ? "disable" : "enable"} ${user.email}?`)) {
      updateStatusMutation.mutate({ userId: user.id, status: newStatus });
    }
  };

  const handleToggleRole = (user: User) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    if (confirm(`Are you sure you want to change ${user.email} role to ${newRole}?`)) {
      updateRoleMutation.mutate({ userId: user.id, role: newRole });
    }
  };

  return (
    <div className="users-root">
      <Card
        className="users-header"
        title="Users Management"
        subtitle="Manage user accounts, roles, and access control"
      >
        <div className="users-actions">
          <Button
            type="button"
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create User
          </Button>
        </div>
      </Card>

      {showCreateModal && (
        <div className="users-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="users-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New User</h3>
            <div className="users-form">
              <label>
                Email *
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </label>
              <label>
                Name
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="User Name"
                />
              </label>
              <label>
                Role
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as "admin" | "user" })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label>
                Status
                <select
                  value={createForm.status}
                  onChange={(e) => setCreateForm({ ...createForm, status: e.target.value as "active" | "disabled" })}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>
              <div className="users-modal-actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Card className="users-table-card">
        {isLoading ? (
          <Skeleton count={5} />
        ) : error ? (
          <div className="users-error">
            Error loading users: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users && users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.name || "-"}</td>
                    <td>
                      <span className={`users-badge users-badge-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span className={`users-badge users-badge-${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleString()
                        : "Never"}
                    </td>
                    <td>
                      <div className="users-actions-cell">
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          onClick={() => setProfileUser(user)}
                        >
                          Profile
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          onClick={() => setSelectedUserId(user.id)}
                        >
                          Diagnostics
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          onClick={() => handleToggleStatus(user)}
                          disabled={updateStatusMutation.isPending}
                        >
                          {user.status === "active" ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          onClick={() => handleToggleRole(user)}
                          disabled={updateRoleMutation.isPending}
                        >
                          {user.role === "admin" ? "Make User" : "Make Admin"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="users-empty">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      {selectedUserId && (
        <UserDiagnosticsModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}

      {profileUser && (
        <UserProfileModal
          user={profileUser}
          onClose={() => setProfileUser(null)}
        />
      )}
    </div>
  );
}

